import { firstNameOf } from '@/lib/name'

// Inline Vapi assistant for the PRD gap-intake voice mode. Extends the
// zero-to-hero/apply vapi-assistant.js pattern (versioned in code, not the
// Vapi dashboard) but is scoped to exactly ONE gap per call — the founder
// taps the orb on the current question, answers it by voice, and the call
// ends itself once that single answer is confirmed back. This keeps the
// voice path exactly as deterministic as the text path (one gap per turn)
// and avoids needing a multi-dimension coverage extraction pass the way the
// z2h/careers interview voice-sync routes do (see
// src/app/api/z2h/prd/intake/voice-sync/route.ts).

export const PRD_VOICE_OPENER = "Let's go through this one by one — I'll ask, you answer."

// `gap` is the single gap this call is scoped to: { question, whyItMatters,
// options }. `gapNumber`/`totalGaps` give the assistant pacing context only
// (it never asks about any gap but this one).
export function buildPrdIntakeAssistant({ founderName, gap, gapNumber = 1, totalGaps = 1 }) {
  const first = firstNameOf(founderName) || ''
  const optionsLine =
    Array.isArray(gap?.options) && gap.options.length > 0
      ? ` A few likely answers, if one fits: ${gap.options.join(', ')}. Or say something else entirely if none of those are right.`
      : ''
  const firstMessage = `${first ? `${first}, question` : 'Question'} ${gapNumber} of ${totalGaps}: ${gap?.question || ''}`

  return {
    name: 'Kingdom Kode PRD Intake',
    firstMessage,
    voice: {
      provider: 'vapi',
      voiceId: 'Elliot',
      fillerInjectionEnabled: false,
    },
    transcriber: { provider: 'deepgram', model: 'nova-2', language: 'en' },
    model: {
      provider: 'openai',
      model: 'gpt-4o',
      temperature: 0.5,
      messages: [
        {
          role: 'system',
          content: `You are helping ${first || 'a founder'} confirm ONE open question on their product requirements document for Kingdom Kode. You ask exactly this single question, nothing else:

"${gap?.question || ''}"
${gap?.whyItMatters ? `\nWhy it matters: ${gap.whyItMatters}` : ''}${optionsLine}

Rules:
- Ask the question once (it's already been said as your opening line). Wait for their answer.
- If the answer is vague or you didn't catch specifics, ask ONE clarifying follow-up — never more than one.
- Once you have a clear, specific answer, repeat it back in one sentence to confirm you heard it right, thank them, and end the call. Do not ask any other question, do not move on to anything else — this call covers only this one item.
- Warm, brief, professional. No filler words ("uh", "um", "you know"). Short sentences.
- Never invent or assume specifics they didn't say.`,
        },
      ],
    },
    startSpeakingPlan: {
      waitSeconds: 0.8,
      smartEndpointingPlan: { provider: 'livekit' },
      transcriptionEndpointingPlan: {
        onPunctuationSeconds: 0.5,
        onNoPunctuationSeconds: 2.5,
        onNumberSeconds: 0.8,
      },
    },
    stopSpeakingPlan: { numWords: 2, voiceSeconds: 0.3, backoffSeconds: 1.0 },
    // The assistant closes the call itself once the single answer is confirmed.
    endCallFunctionEnabled: true,
    maxDurationSeconds: 240,
  }
}
